/* ---------------------------------------------------------------------------
   RUSH ORDER QUANTITY SYNC

   The Rush Order upgrade is a flat fee charged per furniture item, so its
   quantity is a function of the cart, not a choice the shopper makes. The
   variant title still asks them to do the arithmetic — "Please ensure your Rush
   Order QTY matches bed QTY" — which is the bug this removes.

   quantity-selector.liquid hides the control; this keeps the number honest, no
   matter where furniture is added or removed: the cart page, the drawer, a
   quick-buy, or a stale cart from a previous visit.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var RUSH_HANDLE = 'bed-rush-order';

  /* Everything the fee can apply to — beds only. Rush is not offered on the
     stool, so an Artist Stool in the cart must not pull the rush quantity up
     with it. Anything not on this list (stools, pillows, covers, material,
     legs, gift cards) never contributes. */
  var RUSHABLE_TYPES = [
    'curved salon bed',
    'electric salon bed',
    'lash bed',
    'massage table'
  ];

  /* Our own cart:change carries this so the listener below does not answer
     itself. The quantity check would already agree on the second pass, but a
     loop that relies on agreement is a loop. */
  var SELF = 'po:rush-sync';

  var busy = false;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function getJSON(url, options) {
    return fetch(url, options).then(function (response) {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    });
  }

  /* Ready-to-Ship stock ships in 7–10 days. There is no production to shorten,
     so those units must not be charged a rush fee. */
  function isRushable(item) {
    if (item.handle === RUSH_HANDLE) return false;

    var title = (item.product_title || item.title || '').toLowerCase();
    if (title.indexOf('ready to ship') !== -1) return false;

    return RUSHABLE_TYPES.indexOf((item.product_type || '').toLowerCase()) !== -1;
  }

  /* Rush lines are collected rather than assumed unique: a second line can
     appear if one carries a line-item property the other does not. The first
     holds the whole count and any others are zeroed, so the shopper is never
     charged twice for the same bed. */
  function inspect(cart) {
    var lines = [];
    var need = 0;

    (cart.items || []).forEach(function (item) {
      if (item.handle === RUSH_HANDLE) lines.push(item);
      else if (isRushable(item)) need += item.quantity;
    });

    return { lines: lines, need: need };
  }

  function changesFor(state) {
    var changes = [];

    state.lines.forEach(function (line, index) {
      var want = index === 0 ? state.need : 0;
      if (line.quantity !== want) changes.push({ key: line.key, quantity: want });
    });

    return changes;
  }

  /* Sections are bundled the way the theme bundles them, so the drawer, the
     cart count and the savings line all re-render from one response instead of
     drifting out of step with the cart they describe. */
  function applyChanges(changes) {
    var sections = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sections }
      })
    );

    return changes.reduce(function (chain, change) {
      return chain.then(function () {
        return getJSON(root() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: change.key,
            quantity: change.quantity,
            sections: sections.join(',')
          })
        });
      });
    }, Promise.resolve());
  }

  /* The drawer renders from detail.cart.sections[id] and throws on a cart that
     has none, so an unusable cart:change is never sent — a plain refresh goes
     out instead and the drawer refetches for itself. */
  function announce(sections) {
    return getJSON(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(
      function (cart) {
        if (!sections) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return;
        }
        cart.sections = sections;
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: SELF, cart: cart }
          })
        );
      }
    );
  }

  /* While this is rewriting the rush line, the page is still showing remove links
     and quantity fields keyed to the cart as it was a moment ago. A click landing
     in that window sends a line key that no longer exists, Shopify answers "no
     valid id or line parameter", and Prestige prints that under every line — which
     reads as though the bundle refuses to be broken up. Nothing is actually
     un-removable; the controls only have to sit still for the few hundred
     milliseconds the write takes. */
  function lockCart() {
    document.documentElement.classList.add('po-cart-syncing');
  }

  function unlockCart() {
    document.documentElement.classList.remove('po-cart-syncing');
  }

  function onCartPage() {
    return !!(
      window.themeVariables &&
      themeVariables.settings &&
      themeVariables.settings.pageType === 'cart'
    );
  }

  /* The cart's rush upsell. Adds the line at the quantity it should already be —
     one per rushable item — so sync() finds nothing to correct and the shopper
     sees one clean update rather than an add followed by a quantity twitch. */
  function addRush(trigger) {
    if (busy) return Promise.resolve();
    var id = trigger.getAttribute('data-po-rush-add');
    if (!id) return Promise.resolve();

    busy = true;
    trigger.setAttribute('aria-busy', 'true');
    lockCart();

    return getJSON(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (cart) {
        var state = inspect(cart);
        var quantity = state.need > 0 ? state.need : 1;

        var sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sections }
          })
        );

        return getJSON(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(id), quantity: quantity }],
            sections: sections.join(',')
          })
        });
      })
      .then(function (added) {
        /* The cart page renders the ship window, the totals and the line list
           server side, so it is reloaded rather than patched in three places —
           the same choice sync() makes for the same reason. */
        if (onCartPage()) {
          window.location.reload();
          return;
        }
        return announce(added && added.sections);
      })
      .catch(function () {
        trigger.removeAttribute('aria-busy');
      })
      .then(function () {
        busy = false;
        unlockCart();
      });
  }

  /* The order total, on the checkout button. Read out of the Total row rather
     than reformatted from cart.js, so the button and the line above it can never
     disagree about the number or the currency suffix. */
  function decorateCheckout() {
    var recap = document.querySelector('.cart-recap');
    if (!recap) return;

    var button = recap.querySelector('button[name="checkout"]');
    if (!button || button.dataset.poTotal === '1') return;

    var totalRow = null;
    var rows = recap.querySelectorAll('.h-stack');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].querySelector('.text-lg')) { totalRow = rows[i]; break; }
    }
    if (!totalRow) return;

    /* With the Production Deposit chosen the button is not taking the order
       total, it is starting a 50%-plus-shipping charge that Spurit settles at
       checkout. Saying "checkout $7,430" over that would be wrong, and putting
       the deposit figure there would be wrong too — shipping is not known yet.
       So the deposit case gets a label and no number; the block above it already
       states both halves. */
    if (recap.querySelector('[data-po-deposit]')) {
      button.textContent = 'place your deposit';
      button.dataset.poTotal = '1';
      return;
    }

    var values = totalRow.querySelectorAll('.text-lg');
    var total = values.length ? values[values.length - 1].textContent.trim() : '';
    if (!total) return;

    var label = button.textContent.trim();
    button.textContent = label + ' ';
    var span = document.createElement('span');
    span.className = 'po-cta__total';
    span.textContent = '\u00b7 ' + total;
    button.appendChild(span);
    button.dataset.poTotal = '1';
  }

  document.addEventListener('cart:change', decorateCheckout);
  document.addEventListener('cart:refresh', decorateCheckout);
  if (document.readyState !== 'loading') decorateCheckout();
  else document.addEventListener('DOMContentLoaded', decorateCheckout);

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-po-rush-add]');
    if (!trigger) return;
    event.preventDefault();
    addRush(trigger);
  });

  function sync(known) {
    if (busy) return Promise.resolve();

    var start =
      known && known.items
        ? Promise.resolve(known)
        : getJSON(root() + 'cart.js', { headers: { Accept: 'application/json' } });

    return start
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return;

        var state = inspect(cart);
        if (!state.lines.length) return; /* rush not in the cart — nothing to police */

        var changes = changesFor(state);
        if (!changes.length) return;

        busy = true;
        lockCart();

        return applyChanges(changes).then(function (response) {
          /* The cart page renders its own totals, ship dates and savings server
             side, so it is reloaded rather than patched in a dozen places. This
             only ever fires when the numbers actually disagreed. */
          if (onCartPage()) {
            window.location.reload();
            return;
          }

          return announce(response && response.sections);
        });
      })
      .catch(function () {
        /* A quantity that failed to correct is better than a cart page that
           throws. Checkout still charges the rush fee per unit either way. */
      })
      .then(function () {
        busy = false;
        unlockCart();
      });
  }

  document.addEventListener('cart:change', function (event) {
    if (!event.detail || event.detail.baseEvent === SELF) return;
    sync(event.detail.cart);
  });

  document.addEventListener('cart:refresh', function () {
    sync(null);
  });

  /* A cart can also arrive already wrong — carried over from a previous visit,
     or edited in another tab — so it is checked once on load too. */
  if (document.readyState === 'complete') sync(null);
  else window.addEventListener('load', function () { sync(null); });
})();
